import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import {
  ColumnChooserService,
  GridComponent,
  PageService,
  ToolbarService,
  VirtualScrollService,
} from '@syncfusion/ej2-angular-grids';
import {
  AccumulationDataLabelService,
  AccumulationAnnotationService,
} from '@syncfusion/ej2-angular-charts';
import { fadeInOut } from '../../shared/helpers/animations';
import { Utilities } from '../../shared/services/utilities';
import { TooltipComponent } from '@syncfusion/ej2-angular-popups';
import { Subscription, interval } from 'rxjs';
import { SettingsService, SubmissionDashboardService } from '../../shared/services/index';
import { ISubmissionDashboard } from '../../shared/models/submission-dashboard.model';
import BlobUtility from '../../shared/helpers/blob-utilitiy';
import { OrderService } from '../../shared/services/order.service';

@Component({
    selector: 'app-submission-dashboard',
    templateUrl: './submission-dashboard.component.html',
    animations: [fadeInOut],
    providers: [
        ToolbarService,
        ColumnChooserService,
        PageService,
        VirtualScrollService,
        AccumulationDataLabelService,
        AccumulationAnnotationService,
    ],
    styleUrls: ['./submission-dashboard.component.scss'],
    standalone: false
})
export class SubmissionDashboardComponent implements OnInit {
  @ViewChild('submission_grid')
  public submissionGrid: GridComponent;
  @ViewChild('tooltip')
  public tooltip: TooltipComponent;

  @Input() submission_grid_data: ISubmissionDashboard[];
  public toolbarOptions: Object;
  count_submissions: number;
  public clipmodecolumn = { class: 'e-tooltipcolumn' };
  public initialPage: Object;
  loading = true;
  formatOptions: { type: string; format: any };
  formatOptionsWithTime: { type: string; format: string };
  dateRangeDataSource = ['90 days', '180 days', '1 Year', '2 Years', 'All'];
  selectedDateRangeFilter = '1 Year';
  statusProcessing: number = 4; // Submission status code for Submission in Process
  needsToPoll: boolean = true;

  sub: Subscription;

  constructor(private router: Router, private toastr: ToastrService, public submissionDashboardService: SubmissionDashboardService,
    private settingsService: SettingsService, private orderService: OrderService) {}
  ngOnInit() {
    this.toolbarOptions = this.createCustomToolbarItems();
    this.initTabIndex();
    this.initialPage = {
      currentPage: 1,
      pageSize: 25,
      pageSizes: [25, 50, 75, 100, 'All'],
    };
    this.formatOptions = {
      type: 'dateTime',
      format: Utilities.getLocaleDateString(),
    };
    this.formatOptionsWithTime = {
      type: 'dateTime',
      format: Utilities.getLocaleDateString() + ' hh:mm a',
    };

    this.pollData();
  }

  initTabIndex() {
    (document.activeElement as HTMLElement).blur();

    const main = document.getElementById("firstDiv");

    if (main) {
      main.focus();
    }
  }
  pollData() {
    this.getDashboard(); // Start polling immeditely
    // Poll every minute (60000 milliseconds) only if needsToPoll = true
    this.sub = interval(60000).subscribe(() => {
      if (this.needsToPoll) {
        this.getDashboard();
      }
    });
  }

  getDashboard() {
    this.sub = this.submissionDashboardService.getSubmissionDashboard(this.selectedDateRangeFilter).subscribe(data => {
      if (data && data.length) {
        this.loading = false;
        this.submission_grid_data = data;
        this.count_submissions = this.submission_grid_data.length;
        // Get the date x days ago - configurable - submissionDashboardAutoRefreshDays
        const today: Date = new Date();
        // 7 (config number) * 24 * 60 * 60 * 1000 calculates the number of milliseconds in 7 days. We multiply the number of hours in a day (24), the number of minutes in an hour (60),
        // the number of seconds in a minute (60), and the number of milliseconds in a second (1000) to get the total milliseconds in 7 days.
        // today.getTime() - 7 (config number) * 24 * 60 * 60 * 1000 subtracts the number of milliseconds in 7 days from the current date and time,
        // effectively giving us the timestamp for 7 days ago from the current date and time.
        const numberOfDaysAgo: Date = new Date(today.getTime() - parseInt(this.settingsService.submissionDashboardAutoRefreshDays, 10) * 24 * 60 * 60 * 1000);
        const filteredDataStatus4 = this.submission_grid_data.filter(item => item.submissionStatusCode === 4);

        const filteredDataWithConfigDaysAgo = filteredDataStatus4.some(item => {
          const createDate: Date = new Date(item.formatedCreateDate);
          return createDate >= numberOfDaysAgo;
        });

        if (!filteredDataWithConfigDaysAgo) {
          this.needsToPoll = false; // we have nothing that meets the criteria of status 4 and 7 (config number) days go, so we don't need to poll
        }
      } else {
        this.loading = false; // no data so stop the spinner
      }
    },
    error => {
      this.loading = false;
      this.toastr.error(error);
    },
    );
  }

  createCustomToolbarItems() {
    let toolbarItems = [];
    toolbarItems.push('Search');
    return toolbarItems;
  }

  tooltipValue(args) {
    let grid;
    if (args.target.scrollWidth <= args.target.clientWidth) {
      args.cancel = true;
    } else {
      grid = this.submissionGrid;

      const rowInfo = grid.getRowInfo(args.target);
      const rowData = rowInfo.rowData;
      this.tooltip.cssClass = 'customTooltip';
      this.tooltip.content = rowData[(rowInfo.column as any).field];
    }
  }

  removeFileNameExtension(filename: string): string {
    const parts: string[] = filename.split('.');
    parts.pop(); // Remove the last part (file extension)
    return parts.join('.');
  }

  downloadFile(submissionId: number, fileName: string) {
    this.sub = this.submissionDashboardService.downloadFile(submissionId).subscribe(response => {
      let blob = BlobUtility.b64toBlob(response?.data, 'application/zip', 512);
      const url = window.URL.createObjectURL(blob);

      const fileNameWithoutExtension: string = this.removeFileNameExtension(fileName);
      const downloadFileName = `RT-ESG-Msg_${fileNameWithoutExtension}.zip`;

      BlobUtility.setDownloadClickEvent(url, downloadFileName);
    },
    error => {
      this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
    },
    );
  }

  downloadSourceFile(filePath: string, fileName: string) {
    this.sub = this.orderService.downloadFile(filePath, fileName).subscribe(response => {
      const URL = window.URL || (<any>window).webkitURL;
      const downloadUrl = URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.click();
    },
    error => {
      this.toastr.error(error && error.errorMessage ? error.errorMessage : error);
    });
  }

  // Change grid filter operator from "startwith" to "contains" for columns that are string
  public begin(args: any): void {
    if (args.requestType === 'filtering') {
      if (args.columns) {
        args.columns.forEach((col) => {
          switch (args.currentFilteringColumn) {
            case 'version':
              args.currentFilterObject.value = `${args.currentFilterObject.value}.0`;
              break;
            case 'modified':
              break;
            default:
              col.operator = 'contains'; // set the “contains” operator for the column
              break;
          }
        });
      }
    }
  }

  dateRangeSelectionChanged(dateRangeLimitValue: string) {
    this.selectedDateRangeFilter = dateRangeLimitValue;
  }
}
